/**
 * Dashboard API client (ADR-0015 Phase 1). One read of the HR summary:
 * headcount tiles (all roles), org-wide pending-approval counts + upcoming
 * celebrations (owner/admin/hr only — omitted for finance).
 */
import { apiRequest } from './client.js';

export type CelebrationOccasion = 'birthday' | 'work_anniversary';

export interface CelebrationItem {
  employeeId: string;
  name: string;
  occasion: CelebrationOccasion;
  /** YYYY-MM-DD of the upcoming occurrence. */
  date: string;
  daysUntil: number;
  /** work_anniversary only. */
  years?: number;
  isMilestone?: boolean;
  hasEmail: boolean;
  /** Phase 2 — whether a celebration email was already sent this year. */
  sent?: boolean;
  sentAt?: string;
}

export interface CelebrationPreview {
  to: string | null;
  hasEmail: boolean;
  occasion: CelebrationOccasion;
  subject: string;
  body: string;
}

export interface PayrollPulse {
  lastRun: { periodStart: string; periodEnd: string; payDate: string; status: string } | null;
  awaitingApproval: number;
}

export interface OnLeaveEntry {
  name: string;
  leaveType: string;
  startDate: string;
  endDate: string;
}

export interface DashboardSummary {
  headcount: {
    totalStaff: number;
    byCountry: Record<string, number>;
    byPayrollModel: Record<string, number>;
    onLeaveToday: number;
    newHiresThisMonth: number;
  };
  /** All roles (finance's home — Phase 3, D-1a). */
  payrollPulse: PayrollPulse;
  /** owner/admin/hr only. */
  pendingApprovals?: { leave: number; overtime: number; recruitment: number; performance: number };
  /** owner/admin/hr only. */
  celebrations?: { windowDays: number; items: CelebrationItem[] };
  /** owner/admin/hr only. */
  recruitmentPulse?: { openRequisitions: number; inPipeline: number; offersOut: number };
  /** owner/admin/hr only. */
  onLeaveThisWeek?: OnLeaveEntry[];
}

export function getDashboardSummary(): Promise<DashboardSummary> {
  return apiRequest('/v1/dashboard/summary');
}

/** The editable template default for a celebration email (HR may edit it). */
export function previewCelebration(employeeId: string, occasion: CelebrationOccasion): Promise<CelebrationPreview> {
  return apiRequest(`/v1/dashboard/celebrations/${encodeURIComponent(employeeId)}/preview?occasion=${occasion}`);
}

/** Send the (possibly edited) celebration email. Idempotent server-side. */
export function sendCelebration(employeeId: string, body: { occasion: CelebrationOccasion; subject: string; body: string }): Promise<{ occasion: string; occurrenceYear: number; sentAt: string }> {
  return apiRequest(`/v1/dashboard/celebrations/${encodeURIComponent(employeeId)}/send`, { method: 'POST', body });
}
